import { ChartMetaFormField, ContentItem } from 'models/ui';
import { ChartActions, ChartPermission, ErrorCard, ErrorFormField, SectionDivider } from 'components/common';
import { ELSFlex, ELSFlexItem } from 'components/common/els';
import { ChartViewProps } from 'components/features/shared/constants';
import { CONSENT_PARAGRAPHS, FormField, PLACEHOLDER_OPTION_ID, Placeholder, SELECT_DAY_LABEL, SectionTitle, buildClearItem, buildPlaceholderItem } from './constants';
import ConsentForBloodTransfusionSelect from './ConsentForBloodTransfusionSelect';
import './consent.for.blood.transfusion.scss';

export interface ConsentForBloodTransfusionViewProps extends ChartViewProps {
  showSignatureFields: boolean;
  showChartActions?: boolean;
}

const getInlineField = (formField: ChartMetaFormField | undefined): ChartMetaFormField | undefined => {
  if (!formField) return formField;

  return { ...formField, label: '' };
};

const getConditionalRequiredSignatureField = (formField: ChartMetaFormField | undefined, showSignatureFields: boolean): ChartMetaFormField | undefined => {
  if (!formField) return formField;

  // The asterisk marks the field as mandatory, so it has to stay visible once a value is typed in.
  return {
    ...formField,
    isRequired: showSignatureFields
  };
};

/** The backend metadata leaves a blank entry at the top of the day list; swap it for a placeholder plus a Clear action. */
export const withDayDropdownOptions = (formField: ChartMetaFormField | undefined, placeholder: string): ChartMetaFormField | undefined => {
  if (!formField) return formField;

  const existing = formField.chartContent ?? [];
  if (existing.some((item) => item.id === PLACEHOLDER_OPTION_ID)) {
    return formField;
  }

  const isBlankOption = (item: ContentItem): boolean => String(item.value ?? '') === '';
  const realOptions = existing.filter((item) => !isBlankOption(item));
  return { ...formField, label: '', chartContent: [buildPlaceholderItem(placeholder), buildClearItem(), ...realOptions] };
};

/** ErrorFormField renders `children` verbatim when it cannot map the input type, which is how the custom select is injected. */
const asCustomDropdownField = (formField: ChartMetaFormField | undefined): ChartMetaFormField | undefined => (formField ? { ...formField, inputType: undefined } : formField);

const ConsentForBloodTransfusionView = (props: ConsentForBloodTransfusionViewProps) => {
  const { formFieldMap, formSubmittedCount, showSignatureFields, showChartActions = true } = props;

  const daySelect = (field: FormField) => {
    const dayField = withDayDropdownOptions(formFieldMap.get(field), SELECT_DAY_LABEL);

    return (
      <ELSFlexItem className="consent-blood-transfusion__day-item" w="1o5" md="1o1">
        <ErrorFormField formField={asCustomDropdownField(dayField)} formSubmittedCount={formSubmittedCount}>
          <ConsentForBloodTransfusionSelect chartFormField={dayField} placeholder={SELECT_DAY_LABEL} />
        </ErrorFormField>
      </ELSFlexItem>
    );
  };

  const signatureRow = (signatureField: FormField, dayField: FormField) => (
    <ELSFlex className="u-flex-gutter-2x" wrap left>
      <ELSFlexItem w="2o5" md="1o1">
        <ErrorFormField formField={getConditionalRequiredSignatureField(formFieldMap.get(signatureField), showSignatureFields)} formSubmittedCount={formSubmittedCount} />
      </ELSFlexItem>
      {daySelect(dayField)}
    </ELSFlex>
  );

  return (
    <div className="consent-blood-transfusion">
      <h1>{SectionTitle.CONSENT_FOR_BLOOD_TRANSFUSION_FORM}</h1>
      <ErrorCard chartMetaFormFields={formFieldMap} showErrorCard={formSubmittedCount > 0} />

      <SectionDivider mt />

      <div className="consent-blood-transfusion__paragraph">
        <div className="consent-blood-transfusion__sentence-line">
          <span>I</span>
          <div className="consent-blood-transfusion__inline-input consent-blood-transfusion__inline-input--guardian">
            <ErrorFormField
              formField={getInlineField(formFieldMap.get(FormField.PATIENT_LEGAL_GUARDIAN))}
              formSubmittedCount={formSubmittedCount}
              inputProps={{ placeholder: Placeholder.PATIENT_LEGAL_GUARDIAN }}
              useDefaultMargin={false}
              hideAsterisk
            />
          </div>
          <span className="consent-blood-transfusion__sentence-copy">hereby authorize and consent to the administration of blood and/or blood components for</span>
          <div className="consent-blood-transfusion__inline-input consent-blood-transfusion__inline-input--patient-name">
            <ErrorFormField
              formField={getInlineField(formFieldMap.get(FormField.PATIENT_NAME))}
              formSubmittedCount={formSubmittedCount}
              inputProps={{ placeholder: Placeholder.PATIENT_NAME }}
              useDefaultMargin={false}
              hideAsterisk
            />
          </div>
        </div>
        <div className="consent-blood-transfusion__sentence-followup">
          now and/or during the course of therapy, surgery, or illness as deemed advisable by responsible physicians.
        </div>
      </div>

      {CONSENT_PARAGRAPHS.map((paragraph) => (
        <p className="consent-blood-transfusion__paragraph" key={paragraph}>
          {paragraph}
        </p>
      ))}

      <SectionDivider />

      <p className="consent-blood-transfusion__paragraph">I have been given the opportunity to discuss the risks and benefits of transfusion therapy and I:</p>
      <div className="consent-blood-transfusion__decision-row">
        <ErrorFormField
          className="consent-blood-transfusion__decision"
          formField={getInlineField(formFieldMap.get(FormField.DECISION))}
          formSubmittedCount={formSubmittedCount}
          options={{ displayHorizontal: true }}
          useDefaultMargin={false}
          hideAsterisk
        />
        <span>to the use of blood and/or blood components now and/or during the course of treatment.</span>
      </div>

      {showSignatureFields && (
        <div className="consent-blood-transfusion__signatures">
          {signatureRow(FormField.PATIENT_GUARDIAN_SIGNATURE, FormField.PATIENT_GUARDIAN_DAY)}
          <SectionDivider mt />
          {signatureRow(FormField.PROVIDER_DESIGNEE_SIGNATURE, FormField.PROVIDER_DESIGNEE_DAY)}
          <SectionDivider mt />
          {signatureRow(FormField.WITNESS_SIGNATURE, FormField.WITNESS_DAY)}
        </div>
      )}

      {showChartActions && <ChartPermission>{({ allowSaveFragment }) => allowSaveFragment && <ChartActions {...props.chartActionsProps} />}</ChartPermission>}
    </div>
  );
};

export default ConsentForBloodTransfusionView;
